import { twMerge } from 'tailwind-merge';
import { ContentDto } from '@diritto/common/content/dtos/content.dto';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { BadgeMode, BadgePosition, ContentBadge } from 'constants/badge';
import { ZIndex } from 'constants/zIndex';
import { useContentBadges } from 'hooks/useContentBadges';
import { useContentState } from 'state-hooks/contents/useContentState';
import { CoverBadgeIcon, InlineBadgeIcon } from 'ui-components/common/BadgeIcon';
import { HiddenReservationBadge } from 'ui-components/contents/HiddenReservationBadge';

const badgeZ = `z-[${ZIndex.CoverBadge}]`;

const POSITION_STYLES: Record<BadgePosition, string> = {
  [BadgePosition.TOP_LEFT]: `absolute top-0 left-0 flex gap-0.5 p-1 ${badgeZ}`,
  [BadgePosition.TOP_RIGHT]: `absolute top-0 right-0 flex gap-0.5 p-1 ${badgeZ}`,
  [BadgePosition.BOTTOM_LEFT]: `absolute bottom-0 left-0 flex gap-0.5 p-1 ${badgeZ}`,
  [BadgePosition.BOTTOM_RIGHT]: `absolute bottom-0 right-0 flex gap-0.5 p-1 ${badgeZ}`,
  [BadgePosition.INLINE]: 'flex flex-row justify-start items-center gap-[2px] h-6',
};

interface ContentBadgeGroupProps {
  content: ContentDto | ContentPublicRelayDto;
  mode?: BadgeMode;
  className?: string;
}

export const ContentBadgeGroup = ({ content, mode = BadgeMode.COVER, className }: ContentBadgeGroupProps) => {
  const { badgesByPosition } = useContentBadges(content, mode);
  const { reservedHideContentsDiffDays } = useContentState(content);

  const positions = Object.entries(badgesByPosition) as [BadgePosition, ContentBadge[]][];

  if (positions.length === 0) {
    return null;
  }

  const isInline = mode === BadgeMode.TEXT;
  const BadgeIcon = isInline ? InlineBadgeIcon : CoverBadgeIcon;

  const renderBadge = (badge: ContentBadge) => {
    if (badge === ContentBadge.HIDE_RESERVE) {
      return <HiddenReservationBadge key={badge} reservedHideContentsDiffDays={reservedHideContentsDiffDays} />;
    }
    return <BadgeIcon key={badge} type={badge} />;
  };

  return (
    <>
      {positions.map(([position, badges]) => (
        <div key={position} className={twMerge(POSITION_STYLES[position], className)}>
          {badges.map(renderBadge)}
        </div>
      ))}
    </>
  );
};
